<template>
  <div
    v-if="creative"
    class="creative-row"
    :class="`${deviceClass} ${creative.advert_state}`"
  >
    <div class="creative-title">
      <el-checkbox v-if="isStaff" v-model="checked" class="mr-1" />
      <div class="creative-name">
        <span class="creative-id">{{ creative.id }}. </span>
        <span>{{ creative.name }}</span>
        <VerticalLabel
          :id="creative.vertical_id"
          :title="creative.vertical"
          class="info-icon-item"
        />
      </div>
      <CreativeState
        class="ml-1"
        :state="creative.advert_state"
        :reason="creative.status_change_reason"
        :edit-url="`/campaigns/${creative.campaign_id}/creatives/${creative.id}/edit`"
      />
      <CreativeAssetProcessingState
        v-if="creative.show_processing"
        class="ml-1"
        :processed="creative.asset_processed"
        :creature="creative"
      />
      <ViolationsButton
        v-if="isStaff"
        :aggressive="creative.aggressive"
        @click="showMalwareModal"
      />
      <FlatDealActions
        v-if="creative.flat_deal"
        v-model="creative"
        class="ml-1"
      />
    </div>
    <div class="creative-actions ml-1">
      <div class="actions-group mr-1">
        <el-tooltip
          class="item"
          effect="dark"
          placement="top-start"
          content="Edit creative"
        >
          <router-link
            :to="`/campaigns/${creative.campaign_id}/creatives/${creative.id}/edit`"
          >
            <el-button
              type="success"
              plain
              circle
              size="small"
              icon="EditElementIcon"
              class="small-action-btn mr-02"
            />
          </router-link>
        </el-tooltip>
        <el-tooltip
          v-if="isStaff"
          class="item"
          effect="dark"
          placement="top-start"
          content="History"
        >
          <router-link :to="`/history/Creature/${creative.id}`">
            <el-button
              type="info"
              plain
              circle
              size="small"
              icon="TimerElementIcon"
              class="small-action-btn mr-02"
            />
          </router-link>
        </el-tooltip>
        <el-tooltip
          class="item"
          effect="dark"
          placement="top-start"
          content="View Report"
        >
          <el-button
            type="info"
            plain
            circle
            size="small"
            icon="PieChartElementIcon"
            class="small-action-btn mr-02"
            @click="viewReport(creative.id)"
          />
        </el-tooltip>
        <el-tooltip
          class="item"
          effect="dark"
          placement="top-start"
          content="Clone creative"
        >
          <router-link
            :to="`/campaigns/${creative.campaign_id}/creatives/${creative.id}/clone`"
            title="Clone"
          >
            <el-button
              type="primary"
              plain
              circle
              size="small"
              icon="CopyDocumentElementIcon"
              class="small-action-btn"
            />
          </router-link>
        </el-tooltip>
        <template v-if="availableForDelete">
          <el-tooltip
            class="item"
            effect="dark"
            placement="top-start"
            content="Delete"
          >
            <span>
              <el-popconfirm
                confirm-button-text="OK"
                cancel-button-text="No, Thanks"
                confirm-button-type="danger"
                icon="InfoFilledElementIcon"
                icon-color="red"
                :title="`Are you sure to delete '${creative.name}'?`"
                @confirm="invoke('destroy')"
              >
                <template #reference>
                  <el-button
                    type="danger"
                    plain
                    circle
                    size="small"
                    icon="DeleteElementIcon"
                    class="small-action-btn mr-02"
                  />
                </template>
              </el-popconfirm>
            </span>
          </el-tooltip>
        </template>
      </div>
      <template v-for="event in creative.possible_events" :key="event">
        <span v-show="allowedAction(event)">
          <template v-if="event === 'send_to_review'">
            <el-tooltip
              class="item"
              effect="dark"
              placement="top-start"
              :content="sendToReviewButtonTitle"
            >
              <el-button
                size="small"
                type="default"
                icon="ViewElementIcon"
                class="mr-1"
                round
                plain
                @click="showReviewModal = true"
              >
                {{ sendToReviewButtonTitle }}
              </el-button>
            </el-tooltip>
          </template>
          <PossibleActionButton
            v-else
            :event="event"
            :state="creative.advert_state"
            :is-staff="isStaff"
            @invoke="invoke"
          />
        </span>
      </template>
    </div>

    <ReviewConfirmModal
      v-model="showReviewModal"
      :creative-id="creativeId"
      :creative-state="creative.advert_state"
      @confirm="handleReviewConfirm"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, inject } from 'vue'
import { useCreativesStore } from '@/apps/traforama/store/creatives'
import useResize from '@/hooks/useResize'
import CreativeState from './CreativeState.vue'
import FlatDealActions from './FlatDealActions.vue'
import CreativeAssetProcessingState from './CreativeAssetProcessingState.vue'
import PossibleActionButton from '@/apps/traforama/components/PossibleActionButton.vue'
import ReviewConfirmModal from '@/apps/traforama/components/ReviewConfirmModal.vue'
import ViolationsButton from '@/apps/traforama/components/ViolationsButton.vue'
import { NO_FUNDS_STATE, REVIEW_STATE } from '@/apps/traforama/constants'
import { useUserStore } from '@/store/modules/user'
import VerticalLabel from '@/apps/traforama/components/VerticalLabel.vue'

import useRouter from '@/hooks/useRouter'

export default defineComponent({
  name: 'CreativeRow',
  components: {
    CreativeState,
    FlatDealActions,
    CreativeAssetProcessingState,
    PossibleActionButton,
    ReviewConfirmModal,
    VerticalLabel,
    ViolationsButton
  },
  props: {
    creativeId: {
      type: String,
      required: true
    },
    showDelete: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const creativesStore = useCreativesStore()
    const userStore = useUserStore()

    const { isStaff } = userStore
    const creative = computed(() => {
      const creative = creativesStore.items[props.creativeId]
      return creative?.attributes
    })

    const checked = computed({
      get: () => {
        return creativesStore.selected[props.creativeId] !== undefined
      },
      set: (checked: boolean) => {
        creativesStore.ToggleSelection(props.creativeId, checked)
      }
    })

    const invoke = async (event: string, reason?: string) => {
      switch (event) {
        case 'destroy':
          await creativesStore.DestroyItem(props.creativeId)
          break
        default:
          await creativesStore.ChangeState(props.creativeId, event, reason)
      }
    }

    const availableForDelete = computed(() => {
      return (
        props.showDelete ||
        (creative.value !== undefined && creative.value.advert_state === 'new')
      )
    })

    const { deviceClass, isDesktop } = useResize()

    const allowedAction = (event: string) => {
      if (event !== 'send_to_review' || creative.value === undefined) {
        return true
      }
      return ![NO_FUNDS_STATE, REVIEW_STATE].includes(
        creative.value.advert_state
      )
    }

    const { route, router } = useRouter()
    const viewReport = (id: string) => {
      router.push({ path: '/reports/full', query: { creativeId: id } })
    }

    const openMalwareModal = inject('openMalwareModal')

    const showMalwareModal = () => {
      if (openMalwareModal) {
        openMalwareModal({ id: props.creativeId, name: creative.value.name })
      }
    }

    const showReviewModal = ref(false)

    const handleReviewConfirm = async () => {
      await creativesStore.ChangeState(props.creativeId, 'send_to_review')
    }

    const sendToReviewButtonTitle = computed(() => {
      return creative.value?.advert_state === 'rejected'
        ? 'Send to review again'
        : 'Send to review'
    })

    return {
      creative,
      deviceClass,
      invoke,
      checked,
      availableForDelete,
      allowedAction,
      isStaff,
      viewReport,
      showMalwareModal,
      showReviewModal,
      handleReviewConfirm,
      sendToReviewButtonTitle
    }
  }
})
</script>

<style lang="scss" scoped>
.mr-1 {
  margin-right: 1em;
}

.creative-actions {
  display: table;
}

.actions-group {
  float: right;
}

.mobile .campaign-actions {
  display: flex;
  margin-top: 0.5em;
  justify-content: flex-end;
}

.small-action-btn {
  margin-right: 3px;
}

.creative-title {
  display: table;

  .mr-1 {
    margin-right: 1em;
  }
}

.creative-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid var(--el-border-color-light);

  &:last-child {
    border-bottom: none;
  }

  &.paused {
    .creative-name {
      color: var(--el-text-color-secondary);
    }
  }
}

.creative-title {
  display: flex;
  align-items: center;
}

.creative-id {
  color: var(--el-text-color-secondary);
  font-size: 10px;
}

.creative-name {
  font-size: 12px;
  line-height: 2;
}

@media (max-width: 550px) {
  .mobile .creative-title {
    flex-direction: column;
    display: inline-table;
    width: 100%;

    .creative-name {
      margin: 0.5em;
      padding-right: 0.1em;
    }
  }

  .mobile .creative-actions {
    flex-direction: column;
    margin-top: 0.5em;
    justify-content: flex-end;

    .el-button-group {
      margin-bottom: 0.5em;
    }
  }

  .creative-row {
    &.mobile {
      flex-direction: row;
      align-items: flex-start;
    }
  }
}
:deep(.vertical-label) {
  width: 20px;
  display: inline-block;
  vertical-align: middle;
  margin-left: 5px;
}
</style>
